import { DestroyRef, inject, Injectable } from '@angular/core';
import { blobToBase64, getCamera, getMediaRecorder, waitForElement } from './video-utils';
import { BehaviorSubject } from 'rxjs';
import { VideoRecordingManagerService } from '@jump-tech-frontend/domain';

const DATA_FLUSH_INTERVAL_MS = 1000;
const FINALISATION_TIMEOUT_MS = 500;
const POST_RELEASE_GRACE_PERIOD_MS = 200;
const STOP_DELAY_MS = 300;

@Injectable()
export class VideoRecorderService {
  private destroyRef = inject(DestroyRef);
  private videoRecordingManager = inject(VideoRecordingManagerService, { optional: true });

  private recordedChunks: Blob[] = [];
  private stream: MediaStream | undefined;
  private video: HTMLVideoElement;
  private videoId: string;
  private mediaRecorder?: MediaRecorder;

  private flaggedForPause = false;
  private flaggedForStop = false;
  private pendingFinalisation = false;
  private finalisationTimer: ReturnType<typeof setTimeout> | null = null;

  private recordingSubject = new BehaviorSubject<string | null>(null);
  private recording$ = this.recordingSubject.asObservable();

  public init(videoId: string, video?: string) {
    this.videoId = videoId;
    this.loadExistingVideo(video);
    this.updateRecording(video);
    this.registerWithRecordingManager();
    this.registerCleanup();
    return this.recording$;
  }

  public async startRecording() {
    this.resetRecordingState();

    const videoElement = (await waitForElement(this.videoId)) as HTMLVideoElement;
    if (!videoElement) {
      return;
    }

    this.video = videoElement;
    this.prepareVideoElementForRecording();

    this.stream = await getCamera('environment');
    if (!this.stream || this.flaggedForStop) {
      this.flaggedForStop = false;
      return;
    }

    this.video.srcObject = this.stream;
    this.mediaRecorder = getMediaRecorder(this.stream, this.handleDataAvailable, this.handleRecorderStopped);
    if (!this.mediaRecorder) {
      return;
    }

    this.mediaRecorder.start(DATA_FLUSH_INTERVAL_MS);
    if (this.flaggedForPause) {
      this.mediaRecorder.pause();
    }
  }

  public stopRecording() {
    if (this.mediaRecorder?.state === 'paused') {
      this.resumeRecording();
    }
    this.flaggedForStop = true;

    if (this.mediaRecorder?.state === 'recording') {
      this.flushAndStop();
    }
  }

  public pauseRecording() {
    this.flaggedForPause = true;
    this.mediaRecorder?.pause();
  }

  public resumeRecording() {
    this.flaggedForPause = false;
    this.mediaRecorder?.resume();
  }

  public async removeRecording() {
    const videoElement = (await waitForElement(this.videoId)) as HTMLVideoElement;
    if (!videoElement) {
      return;
    }
    videoElement.srcObject = null;
    videoElement.load();
  }

  public updateRecording(video?: string) {
    this.recordingSubject.next(video ?? null);
  }

  public isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording' || this.mediaRecorder?.state === 'paused';
  }

  private handleDataAvailable = (e: BlobEvent) => {
    if (e.data.size > 0) {
      this.recordedChunks.push(e.data);
    }

    if (this.isRecorderInactiveAndPendingFinalisation()) {
      this.cancelFinalisationTimer();
      this.finaliseRecording();
      return;
    }

    if (this.flaggedForPause && this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder?.pause();
      this.flaggedForPause = false;
    }
  };

  private handleRecorderStopped = () => {
    this.pendingFinalisation = true;
    this.startFinalisationTimer();
  };

  private isRecorderInactiveAndPendingFinalisation(): boolean {
    return this.pendingFinalisation && this.mediaRecorder?.state === 'inactive';
  }

  private startFinalisationTimer() {
    this.finalisationTimer = setTimeout(() => {
      if (this.pendingFinalisation) {
        this.releaseMediaResources();
        setTimeout(() => {
          if (this.pendingFinalisation) {
            this.finaliseRecording();
          }
        }, POST_RELEASE_GRACE_PERIOD_MS);
      }
    }, FINALISATION_TIMEOUT_MS);
  }

  private cancelFinalisationTimer() {
    if (this.finalisationTimer) {
      clearTimeout(this.finalisationTimer);
      this.finalisationTimer = null;
    }
  }

  private flushAndStop() {
    this.mediaRecorder?.requestData();
    setTimeout(() => {
      if (this.mediaRecorder?.state === 'recording') {
        this.mediaRecorder.stop();
      }
    }, STOP_DELAY_MS);
  }

  private finaliseRecording() {
    if (!this.pendingFinalisation) {
      return;
    }
    this.pendingFinalisation = false;

    this.releaseMediaResources();

    if (this.recordedChunks.length === 0) {
      return;
    }

    const blob = new Blob(this.recordedChunks, { type: 'video/mp4' });
    this.displayRecordedVideo(blob);
    this.saveRecordingAsBase64(blob);
  }

  private releaseMediaResources() {
    if (this.video) {
      this.video.srcObject = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
      });
      this.stream = undefined;
    }
  }

  private displayRecordedVideo(blob: Blob) {
    if (this.video) {
      this.video.src = URL.createObjectURL(blob);
      this.video.autoplay = false;
      this.video.controls = true;
      this.video.muted = false;
      this.video.load();
    }
  }

  private saveRecordingAsBase64(blob: Blob) {
    blobToBase64(blob, 'video/mp4').then((base64: string) => {
      this.updateRecording(base64);
    });
  }

  private resetRecordingState() {
    this.recordedChunks = [];
    this.flaggedForPause = false;
    this.flaggedForStop = false;
  }

  private prepareVideoElementForRecording() {
    this.video.autoplay = true;
    this.video.controls = false;
    this.video.muted = true;
  }

  private loadExistingVideo(video?: string) {
    if (video?.length) {
      waitForElement(this.videoId).then(videoElement => {
        this.video = videoElement as HTMLVideoElement;
        if (this.video && !this.video.src) {
          this.video.src = video ?? '';
          this.video.load();
        }
      });
    }
  }

  private registerWithRecordingManager() {
    if (this.videoRecordingManager) {
      this.videoRecordingManager.registerRecording({
        id: this.videoId,
        stopRecording: () => this.stopRecording(),
        isRecording: () => this.isRecording()
      });
    }
  }

  private registerCleanup(): void {
    try {
      this.destroyRef.onDestroy(() => this.performCleanup());
    } catch {
      this.performCleanup();
    }
  }

  private performCleanup(): void {
    try {
      this.stopRecording();
      if (this.videoRecordingManager) {
        this.videoRecordingManager.unregisterRecording(this.videoId);
      }
    } catch {
      // Cleanup errors are non-critical
    }
  }
}
